// Cross-domain language switching utilities

import { getLocalizedPath, getRouteByKey } from './routing';

export function isDevHostname(hostname: string): boolean {
  const host = hostname.toLowerCase().split(':')[0];
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host.endsWith('.localhost') ||
    host.includes('replit.dev') ||
    host.includes('replit.app')
  );
}

export type Language = 'sk' | 'cs' | 'da' | 'en' | 'de';

export const LANGUAGE_TO_DOMAIN: Record<Language, string> = {
  sk: 'bidmio.sk',
  cs: 'bidmio.cz',
  da: 'bidmio.dk',
  en: 'bidmio.com',
  de: 'bidmio.de'
};

export const DOMAIN_TO_LANGUAGE: Record<string, Language> = {
  'bidmio.sk': 'sk',
  'www.bidmio.sk': 'sk',
  'bidmio.cz': 'cs',
  'www.bidmio.cz': 'cs',
  'bidmio.dk': 'da',
  'www.bidmio.dk': 'da',
  'bidmio.com': 'en',
  'www.bidmio.com': 'en',
  'bidmio.de': 'de',
  'www.bidmio.de': 'de'
};

/**
 * Get available languages for the current domain
 * - .sk, .cz, .dk domains: Show native language + English
 * - .com domain: Show all 4 languages
 */
export function getAvailableLanguagesForDomain(currentDomain: string, currentLang: Language): Array<{ code: Language; name: string }> {
  const allLanguages = [
    { code: 'sk' as Language, name: 'Slovenčina' },
    { code: 'cs' as Language, name: 'Čeština' },
    { code: 'da' as Language, name: 'Dansk' },
    { code: 'en' as Language, name: 'English' },
    { code: 'de' as Language, name: 'Deutsch' }
  ];

  // For .com and local/dev hosts, show all languages
  if (currentDomain.includes('bidmio.com') || isDevHostname(currentDomain)) {
    return allLanguages;
  }

  // For local domains (.sk, .cz, .dk), show native language + English
  const nativeLang = DOMAIN_TO_LANGUAGE[currentDomain] || currentLang;
  return allLanguages.filter(lang => lang.code === nativeLang || lang.code === 'en');
}

/**
 * Get the primary language for a domain
 */
export function getPrimaryLanguageForDomain(domain: string): Language {
  return DOMAIN_TO_LANGUAGE[domain] || 'en';
}

/**
 * Build cross-domain redirect URL for language switching
 */
export function buildLanguageSwitchUrl(
  currentPath: string,
  targetLang: Language,
  protocol: string = 'https'
): string {
  // Get the localized path for the target language
  const localizedPath = getLocalizedPath(currentPath, targetLang);
  
  // Get the target domain
  const targetDomain = LANGUAGE_TO_DOMAIN[targetLang];
  
  // Build the full URL
  return `${protocol}://www.${targetDomain}${localizedPath}`;
}

/**
 * Handle language switch with cross-domain redirect
 */
export function handleLanguageSwitch(targetLang: Language): void {
  const currentHostname = window.location.hostname.toLowerCase();
  
  if (isDevHostname(currentHostname)) {
    // Development: stay on this host, localize the path, and set ?lang=
    const currentPath = window.location.pathname;
    const currentSearch = new URLSearchParams(window.location.search);
    currentSearch.set('lang', targetLang);
    const localizedPath = getLocalizedPath(currentPath, targetLang);
    const dest = localizedPath || getRouteByKey('home', targetLang) || '/';
    window.location.href = `${dest}?${currentSearch.toString()}`;
  } else {
    // Production: Cross-domain redirect
    const currentPath = window.location.pathname;
    const redirectUrl = buildLanguageSwitchUrl(currentPath, targetLang);
    window.location.href = redirectUrl;
  }
}
